import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PublicLayout from '../../../Layouts/Shared/_PublicLayout';

/* ─── Design tokens ─── */
const T = {
  navy: '#0a2540',
  blue: '#0050d8',
  blueLt: '#e8f0fe',
  border: '#d9dde3',
  bg: '#f5f7fa',
  muted: '#4a5568',
  light: '#6b7a8d',
};

const ROUTES = { home: '/' };
const COMMON = 'public.static.static-pages.common';

/* ─────────────────────────────────────────────
   TENANT DEFAULT DEĞERLERİ
   Prop gelmezse → dil dosyası denenir (t('tenant.x'))
   O da boşsa → buradaki sabit değer kullanılır.
   Tüm fallback'ler tek yerden yönetilir.
───────────────────────────────────────────── */
const TENANT_DEFAULTS = {
  brandName:            'Tatalli Cloud',
  brandNameFromOwn:     'Şirketten',
  brandNameToOwn:       'Şirkete',
  tenantBrandName:      'Tatalli',
  companyName:          'Tatalli Teknoloji ve Ticaret Ltd. Şti.',
  companyAddress:       'ÜNALAN MAH. LİBADİYE CAD. EMAR RESIDENCE BLOK NO: 82E İÇ KAPI NO: 291 ÜSKÜDAR / İSTANBUL',
  companyTradingNumber: '435859-5',
  companyEmail:         'info@tatalli.com.tr',
  companyPhone:         '+90 (850) 309 2950',
  companyKep:           'tatalli@hs01.kep.tr',
  companyMobileApp:     'Tatalli',
  webSiteWithWww:       'cloud.tatalli.com.tr',
  webSiteMobileApp:     'Tatalli Cloud Mobil',
};

/* Prop → dil dosyası → sabit default sırasıyla çözümler */
function useTenant(props) {
  const { t } = useTranslation();
  const keys = {
    brandName:            'tenant.company-brand-name',
    brandNameFromOwn:     'tenant.company-brand-name-from-own',
    brandNameToOwn:       'tenant.company-brand-name-to-own',
    tenantBrandName:      'tenant.brand-name',
    companyName:          'tenant.company-name',
    companyAddress:       'tenant.company-address',
    companyTradingNumber: 'tenant.company-trading-number',
    companyEmail:         'tenant.company-email-address',
    companyPhone:         'tenant.company-phone-number',
    companyKep:           'tenant.company-kep-address',
    companyMobileApp:     'tenant.company-mobile-app-name',
    webSiteWithWww:       'tenant.web-site-with-www',
    webSiteMobileApp:     'tenant.web-site-mobile-app-name',
  };

  const resolved = {};
  Object.entries(keys).forEach(([field, i18nKey]) => {
    if (props[field]) {
      resolved[field] = props[field];
    } else {
      const fromI18n = t(i18nKey);
      resolved[field] = (fromI18n && fromI18n !== i18nKey)
        ? fromI18n
        : TENANT_DEFAULTS[field];
    }
  });
  return resolved;
}

function Breadcrumb({ label, color }) {
  const { t } = useTranslation();
  const c = color || T.light;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, fontSize: 13, color: c }}>
      <Link to={ROUTES.home} style={{ color: c, textDecoration: 'none' }}>
        {t(`${COMMON}.breadcrumb_home`)}
      </Link>
      <i className="fas fa-chevron-right" style={{ fontSize: 10 }} />
      <span>{label}</span>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ANA BILEŞEN
   tenant değerleri dil dosyasından DEĞİL,
   direkt prop olarak geçilir.
───────────────────────────────────────────── */
export default function ProtectionOfPersonalDataPage(props) {
  const { t } = useTranslation();
  const p = 'public.static.static-pages.protection-of-personal-data-page';

  /* prop → dil dosyası → TENANT_DEFAULTS sırası ile çözümle */
  const tenant = useTenant(props);

  return (
    <PublicLayout title={t(`${p}.meta_title`)}>
      <style>{`
        .ppd-hero { background: ${T.bg}; border-bottom: 1px solid ${T.border}; padding: 36px 0; }
        .ppd-body { padding: 36px 0; }
        .ppd-content { max-width: 860px; font-size: 14px; color: ${T.muted}; line-height: 1.8; }
        .ppd-content h2 { font-size: 20px; font-weight: 700; color: ${T.blue}; margin: 32px 0 12px; }
        .ppd-content p { margin-bottom: 12px; }
        .ppd-content ul { margin: 8px 0 12px 20px; }
        .ppd-content li { margin-bottom: 6px; }
        .ppd-content strong { color: ${T.navy}; }
        @media (max-width: 768px) { .ppd-hero, .ppd-body { padding: 28px 0; } }
        @media (max-width: 480px) { .ppd-hero, .ppd-body { padding: 20px 0; } }
      `}</style>

      {/* Hero */}
      <div className="ppd-hero">
        <div className="pl-container">
          <Breadcrumb label={t(`${p}.breadcrumb`)} />
          <h1 style={{ fontSize: 'clamp(22px,4vw,32px)', fontWeight: 700, color: T.navy, marginBottom: 12 }}>
            {t(`${p}.hero_title`)}
          </h1>
          <p style={{ fontSize: 15, color: T.muted, margin: 0, maxWidth: 520 }}>
            {t(`${p}.hero_desc`)}
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="ppd-body">
        <div className="pl-container">
          <div className="ppd-content">
            <KvkkBolumu tenant={tenant} />
            <AcikRizaFormu tenant={tenant} />
            <GizlilikPolitikasi tenant={tenant} />
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}

/* ═══════════════════════════════════════════════
   BÖLÜM 1 – KVKK AYDINLATMA METNİ
═══════════════════════════════════════════════ */
function KvkkBolumu({ tenant }) {
  const { brandName, brandNameFromOwn, brandNameToOwn, companyName, companyAddress,
          companyTradingNumber, companyEmail, webSiteWithWww, webSiteMobileApp, companyMobileApp } = tenant;

  const islemAmaclari = [
    'Üyelik işlemlerinizin gerçekleştirilmesini sağlamak, alışveriş işlemlerinizin gerçekleştirilmesine aracılık etmek ve alışveriş kayıtlarını tutabilmek ve üyelik ve alışveriş süreçlerine ilişkin işlemleri kanuna uygun olarak yürütebilmek,',
    'Sözleşme ilişkisi gereği iletişim kurmak için adres ve diğer gerekli bilgileri kaydetmek,',
    'Elektronik (internet sitesi /mobil uygulama vs.) veya kağıt ortamında işleme dayanak olacak tüm kayıt ve belgeleri düzenlemek,',
    'İnternet sitemiz ve/veya mobil uygulamamızda gerçekleştirilen faaliyetlere ilişkin olarak resmi kurumlardan gelen talepleri yerine getirebilmek,',
    'İnternet sitemiz ve/veya mobil uygulamamızdaki ürün ve hizmetlerin yer aldığı sayfaların kontrolünü yapabilmek,',
    'Müşteri hizmetleri faaliyetlerimizi (taleplerin yanıtlanması, kalite kontrolü vb.) gerçekleştirebilmek ve hizmetlerimiz ile ilgili müşteri şikâyet ve önerilerini değerlendirebilmek,',
    'İletişim tercihlerinizi dikkate alarak, izin vermeniz kaydıyla, çeşitli pazarlama ve reklam faaliyetlerinde kullanabilmek; kupon, indirim ve özel promosyonlar sunmak; ilgi alanlarınızı dikkate alarak ilgilenebileceğiniz ürünler hakkında bilgi vermek ve kampanyaları aktarmak; çapraz satış yapılması, hedef kitle belirlenmesi, müşteri hareketleriniz takip edilerek kullanıcı deneyiminizi arttırıcı faaliyetlerin yürütmek anket, profilleme, istatistiksel çalışmalar yapmak; anketler veya soru formları ile görüşlerinizi öğrenmek için direkt ya da anlaşmalı kuruluşlar aracılığıyla sizinle e-posta, telefon, SMS veya posta yoluyla iletişime geçmek,',
    'İzin vermeniz kaydıyla, üçüncü kişiler tarafından çerezler aracılığıyla pazarlama faaliyetinde bulunulmasını mümkün kılmak,',
    'Yasal yükümlülüklerimizi yerine getirebilmek ve yürürlükteki mevzuattan doğan haklarımızı kullanabilmek,',
    'Hile veya dolandırıcılık teşkil eden, güvenlik ihlali oluşturan veya şüpheli ya da hukuka aykırı olan faaliyetleri önlemek, tespit etmek, en aza indirmek ve soruşturmak, işlem güvenliğini takip edebilmek için log, IP ve trafik kayıtlarını tutabilmek,',
    'İnternet sitemiz ve/veya mobil uygulamamızın kullanımına ilişkin herhangi bir uyuşmazlığın meydana gelmesi durumunda uyuşmazlıkların çözülmesini sağlamak,',
    'İnternet sitemiz ve/veya mobil uygulamamızı ve hizmetlerimizi sizler için özelleştirmek, ölçmek ve geliştirmek,',
    'Web sitesi ve/veya mobil uygulamalarda yaşanan hataları takip edebilmek ve izleyebilmek,',
    'Kara Paranın Aklanmasının önlenmesi mevzuatına ve yurtiçi ve uluslararası mevzuata uyum sağlamak,',
    'Ödeme işlemlerinizin gerçekleştirilmesine aracılık etmek, ödemeleri tahsil etmek ve ödemelerin satıcılara transfer edilmesini sağlamak,',
    'Satın aldığınız ürün ve hizmetlerin satıcılar tarafından alıcılara gönderilmesini sağlamak için satıcılarla iletişim kurmanızı sağlamak,',
    'Satıcılara faturaların gönderimini sağlamak,',
    'Pazarlama, tanıtım faaliyetlerinde kullanılmasını sağlamak,',
    'Alışverişlerinde banka puanlarını kullanabilecekleri ürünlere ve ürün kategorilerine ilişkin müşterileri bilgilendirmek,',
    'Müşteri memnuniyetini sağlayabilmek ve müşteri taleplerini yanıtlanması, kimlik/harcama teyidini ve ücret iadelerini gerçekleştirmek amacıyla hesabınız ile ilgili size bildirimde bulunmak, hesabınız ile ilgili sorunları gidermek, uyuşmazlıkları çözmek veya müşteri hizmetleri faaliyeti kapsamında gerekli olabilecek diğer hizmetleri sağlamak.',
  ];

  const haklari = [
    'Kişisel veri işlenip işlenmediğini öğrenme,',
    'Kişisel verileri işlenmişse buna ilişkin bilgi talep etme,',
    'Kişisel verilerin işlenme amacını ve bunların amacına uygun kullanılıp kullanılmadığını öğrenme,',
    'Yurt içinde veya yurt dışında kişisel verilerin aktarıldığı üçüncü kişileri bilme,',
    'Kişisel verilerin eksik veya yanlış işlenmiş olması hâlinde bunların düzeltilmesini isteme ve bu kapsamda yapılan işlemin kişisel verilerin aktarıldığı üçüncü kişilere bildirilmesini isteme,',
    'Kanun ve ilgili diğer mevzuata uygun olarak işlenmiş olmasına rağmen, işlenmesini gerektiren sebeplerin ortadan kalkması halinde kişisel verilerin silinmesini veya yok edilmesini isteme ve bu kapsamda yapılan işlemin kişisel verilerin aktarıldığı üçüncü kişilere bildirilmesini isteme,',
    'İşlenen verilerin münhasıran otomatik sistemler vasıtasıyla analiz edilmesi suretiyle kişinin kendisi aleyhine bir sonucun ortaya çıkmasına itiraz etme,',
    'Kişisel verilerin kanuna aykırı olarak işlenmesi sebebiyle zarara uğraması halinde zararın giderilmesini talep etme haklarına sahiptir.',
  ];

  return (
    <>
      <h2 id="kvkk">Kişisel Verilerin Korunması</h2>
      <p>Kişisel Verilerin Korunması ve İşlenmesi Şartları Hakkında Aydınlatma Metni</p>
      <p>
        {companyName} ({'\u201c'}<strong>{brandName}</strong>{'\u201d'}) olarak, işbu Aydınlatma Metni ile,
        Kişisel Verilerin Korunması Kanunu ({'\u201c'}<strong>Kanun</strong>{'\u201d'}) ve Aydınlatma
        Yükümlülüğünün Yerine Getirilmesinde Uyulacak Usul ve Esaslar Hakkında Tebliğ kapsamında
        aydınlatma yükümlüğümüzün yerine getirilmesi amaçlanmaktadır.
      </p>
      <p>Bu kapsamda bilgi vermekle yükümlü olduğumuz konular aşağıdaki gibidir:</p>

      <ul style={{ listStyleType: 'lower-latin' }}>
        {/* (a) */}
        <li>
          <strong>Veri Sorumlusu ve Varsa Temsilcisinin Kimliği</strong><br />
          Veri sorumlusu; {companyAddress} adresinde mukim, {companyTradingNumber} sicil
          numarası ile kayıtlı <strong>{companyName}</strong>&apos;dir.
        </li>

        {/* (b) */}
        <li>
          <strong>Kişisel Veri Toplamanın Yöntemi ve Hukuki Sebebi</strong><br />
          Şirket tarafından {webSiteWithWww} internet sitesi veya {webSiteMobileApp} ve{' '}
          {companyMobileApp} Mobil uygulamaları üzerinden yürütülen faaliyetlerle ilgili
          olarak, {brandNameToOwn} verdiğiniz kimlik bilgileri (isim, soyisim, doğum tarihi,
          uyruk, T.C. kimlik numarası, cinsiyet, pasaport geçerlilik tarihi, pasaport numarası),
          iletişim bilgileri (e-posta adresi, fatura, cep telefonu numarası); finans bilgileri
          (kredi kartı bilgileri, banka puan bilgileri), işitsel veriler (çağrı merkezi kayıtları),
          işlem güvenliği bilgileri (IP Adresi Bilgileri, satıcı kodu), plaka bilgisi, cinsiyet
          bilgisi ve mensup bilgisi kategorisindeki kişisel verileriniz ve {brandNameToOwn} üyelik
          kapsamında sunduğunuz kişisel verileriniz, üyelik formu, mobil uygulama ve internet sitesi
          veya dijital uygulamalar, sosyal medya, çağrı merkezi, müşteri görüşmeleri, SMS,
          web sitelerine yapılan yazılı/dijital başvurular, Şirketimize yapılan yazılı/dijital
          başvurular, çağrı merkezi hizmeti alınan şirketler, {companyName} dahil anlaşmalı
          firmalar ve iş ortakları gibi Şirketimizin sizlerle iletişime geçtiği kanallar
          aracılığıyla toplanıp yasal süresi veya veri işleme amacının gerektirdiği süre boyunca
          saklanmaktadır. Bu yöntemlerle toplanan kişisel verileriniz Kanun&apos;un 5. maddesi
          çerçevesinde, gerekli olması halinde varsa açık rıza verdiğiniz işleme faaliyetlerinin
          gerçekleştirilmesi, kanunlarda öngörülmesi, sözleşmenin kurulması veya ifası,{' '}
          {brandNameFromOwn} hukuki yükümlülüklerini yerine getirebilmesi ve ilgili kişinin temel
          hak ve özgürlüklerine zarar verilmemesi kaydıyla {brandNameFromOwn} meşru menfaatleri
          için veri işlemenin zorunlu olması hukuki sebepleri ile toplanmaktadır.
        </li>

        {/* (c) */}
        <li>
          <strong>Kişisel Verilerin Hangi Amaçla İşleneceği</strong><br />
          {brandName} kişisel verileri aşağıdaki amaçlarla işlemektedir:
          <ul style={{ listStyleType: 'disc' }}>
            {islemAmaclari.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        </li>

        {/* (d) */}
        <li>
          <strong>İşlenen Kişisel Verilerin Kimlere ve Hangi Amaçla Aktarılabileceği</strong><br />
          Toplanan kişisel verileriniz yukarıda belirtilen amaçların gerçekleştirilmesi ile sınırlı
          olmak üzere; {brandNameFromOwn} iş ortaklarına, hissedarlarına, iştiraklerine ve alt
          kuruluşlarına, hizmet alınan firmalara, bulut sistemlerinde depolanmasına bağlı olarak
          yurt dışında bulunan anlaşmalı sunucu sistemlerine; kamu kurum ve kuruluşlarına; bağımsız
          denetim şirketlerine; anlaşmalı ödeme ve/veya elektronik para kuruluşlarına; Mastercard,
          Visa, BKM gibi ulusal ve uluslararası kart kuruluşlarına; anlaşmalı kargo ve kurye
          şirketlerine; ticari elektronik ileti aracı hizmet sağlayıcılarına; çerez (cookie)
          hizmeti alınan iş ortaklarına; pazar araştırması yapacak şirketlerle
          aktarılabilecektir. Bu Aydınlatma Metni&apos;nde belirtilen kişisel verileriniz yukarıda
          belirtilen işleme amaçlarının gerçekleştirilmesi ile sınırlı olmak üzere Kanun&apos;a ve
          Kişisel Verilerin Yurt Dışına Aktarılmasına İlişkin Usul ve Esaslar Hakkında
          Yönetmelik&apos;e uygun şekilde yurt dışında mukim firmalara aktarılmaktadır.
        </li>

        {/* (e) */}
        <li>
          <strong>Kişisel Veri Sahibinin Kanun&apos;un 11. Maddesinde Sayılan Hakları</strong><br />
          Kişisel veri sahipleri olarak, haklarınıza ilişkin taleplerinizi aşağıda düzenlenen
          yöntemlerle {brandNameToOwn} iletmeniz durumunda {brandName} talebin niteliğine göre
          talebi en kısa sürede ve en geç 30 (otuz) gün içerisinde ücretsiz olarak
          sonuçlandıracaktır. Ancak, işlemin ayrıca bir maliyeti gerektirmesi halinde, {brandName}{' '}
          tarafından Kişisel Verileri Koruma Kurulu&apos;nca belirlenen tarifedeki ücret alınacaktır.
          Bu kapsamda kişisel veri sahipleri;
          <ul style={{ listStyleType: 'disc' }}>
            {haklari.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        </li>
      </ul>

      <p>
        Kanun&apos;un 13. maddesinin 1. fıkrası gereğince, yukarıda belirtilen haklarınızı kullanmak
        ile ilgili talebinizi yazılı olarak veya Kişisel Verileri Koruma Kurulu&apos;nun belirlediği
        diğer yöntemlerle {brandNameToOwn} iletebilirsiniz. Bu hakkınızı kullanmak için kimliğinizi
        tespit edici gerekli bilgiler ile kullanmayı talep ettiğiniz hakkınıza yönelik
        açıklamalarınızı içeren talebinizi; {brandName} nezdinde daha önceden kayıtlı bulunan
        e-posta adresiniz üzerinden {brandNameFromOwn} {companyEmail} adresine elektronik posta
        yoluyla iletebilirsiniz.
      </p>
      <p><strong>{companyName}</strong></p>
    </>
  );
}

/* ═══════════════════════════════════════════════
   BÖLÜM 2 – AÇIK RIZA FORMU
═══════════════════════════════════════════════ */
function AcikRizaFormu({ tenant }) {
  const { brandName, brandNameFromOwn, brandNameToOwn, companyName,
          webSiteWithWww, webSiteMobileApp, companyMobileApp } = tenant;
  return (
    <>
      <h2>Kişisel Verilerin İşlenmesi ve Aktarılmasına İlişkin Açık Rıza Formu</h2>
      <p>
        {companyName} ({'\u201c'}{brandName}{'\u201d'}) tarafından {webSiteWithWww} internet sitesi,{' '}
        {webSiteMobileApp} ve {companyMobileApp} Mobil uygulamaları üzerinden yürütülen faaliyetlerle
        ilgili olarak, {brandNameToOwn} sağlamış olduğum kimlik bilgilerim (isim, soyisim, doğum
        tarihi, uyruk, T.C. kimlik numarası, cinsiyet, pasaport geçerlilik tarihi, pasaport numarası),
        iletişim bilgilerim (e-posta adresi, fatura, cep telefonu numarası); finans bilgilerim (kredi
        kartı bilgileri), işlem güvenliği bilgileri (IP Adresi Bilgileri, satıcı kodu), plaka bilgisi,
        cinsiyet bilgisi ve mensup bilgisi kategorisindeki kişisel verilerimin, Ürün/hizmet teklifi,
        pazarlama ve kampanya faaliyetlerinde kullanılabilmesi ve bununla ilgili iletişime geçilmesi
        için {brandNameFromOwn} ve grup şirketleri dahil iş ortaklarına ve/veya tedarikçilerine
        aktarılmasına izin veriyorum.
      </p>
    </>
  );
}

/* ═══════════════════════════════════════════════
   BÖLÜM 3 – GİZLİLİK POLİTİKASI
═══════════════════════════════════════════════ */
function GizlilikPolitikasi({ tenant }) {
  const { companyName, webSiteWithWww } = tenant;
  return (
    <>
      <h2 id="privacy-policy">Gizlilik Politikası</h2>
      <p>
        İşbu Gizlilik Politikası, {companyName}&apos;ne (&quot;Şirket&quot;) ait aralarında{' '}
        {webSiteWithWww}&apos;un da bulunduğu internet siteleri ve mobil uygulamaları/platformları
        (&quot;Platform&quot;) kullanan tüm kişiler (&quot;Kullanıcı&quot;) için geçerlidir.
      </p>
      <ul style={{ listStyleType: 'decimal' }}>
        <li>
          Şirket, elektronik ticaret işlemleri nedeniyle elde ettiği veya Kullanıcılar tarafından
          üyelik formları ile veya sair surette kendisine iletilen kişisel verileri ve bilgileri,
          işbu Gizlilik Politikası ve Platform&apos;da yer alan, kişisel verilere ilişkin ilgili
          Aydınlatma Metni&apos;nde belirtilen haller haricinde üçüncü şahıslarla paylaşmamakta ve
          belirtilen amaçlar dışındaki hiçbir ticari amaçla kullanmamakta ve üçüncü kişilere
          aktarmamaktadır.
        </li>
        <li>
          Kullanıcılar&apos;a ait kişisel veriler ve gizli bilgiler; ancak resmi makamlarca talep
          edilmesi halinde ve yürürlükteki emredici mevzuat hükümleri gereğince açıklama yapılmak
          zorunda olunduğu durumlarda resmi makamlara açıklanacaktır.
        </li>
        <li>
          Kullanıcılar tarafından Şirket&apos;e sağlanan veya Şirket tarafından edinilen bilgilerin
          ve Platform üzerinden gerçekleşen tüm işlemlerin güvenliği için bilgi ve işlemin
          mahiyetine göre Şirketimiz veya ilgili kuruluşça sistemlerde ve internet altyapısında,
          teknolojik imkânlar ve maliyet unsurları dâhilinde, uygun teknik ve idari tedbirler
          alınmıştır.
        </li>
        <li>
          Kullanıcı&apos;nın, Şirket ile paylaşmış olduğu kişisel verilerinin Kullanıcı&apos;ya
          ait olduğu kabul edilmektedir.
        </li>
        <li>
          Şirket, işbu Gizlilik Politikası&apos;nın hükümlerini dilediği zaman güncelleyebilir,
          değiştirebilir veya yürürlükten kaldırabilir. Güncellenen, değiştirilen ya da yürürlükten
          kaldırılan her hüküm, yayın tarihinde Kullanıcı için hüküm ifade edecektir. Yapılan
          değişikliğin ardından Kullanıcı&apos;nın Platform&apos;u kullanmaya devam ediyor olması,
          değişikliklere muvafaketi anlamına gelecektir.
        </li>
      </ul>
      <p>Bilgilerinize sunarız.</p>
      <p><strong>{companyName}</strong></p>
    </>
  );
}

/* ═══════════════════════════════════════════════
   BÖLÜM 4 – ÜYELİK SÖZLEŞMESİ
═══════════════════════════════════════════════ */
function UyelikSozlesmesi({ tenant }) {
  const { brandName, brandNameFromOwn, brandNameToOwn, tenantBrandName, companyName,
          companyAddress, companyEmail, companyPhone, companyKep,
          companyMobileApp, webSiteWithWww } = tenant;
  return (
    <>
      <h2 id="membership-contract">Üyelik Sözleşmesi</h2>
      <p><strong>{brandName} Üyelik Sözleşmesi</strong></p>
      <p>
        İşbu Üyelik Sözleşmesi (&quot;Sözleşme&quot;), {companyAddress} adresinde mukim {companyName}{' '}
        (&quot;{brandName}&quot;) ve ÜYE arasında {webSiteWithWww} ve mobil uygulamanın (Platform)
        kullanımına ilişkin esas ve koşullar ile tarafların hak ve yükümlülüklerini düzenlemek amacıyla
        akdedilmiştir. &quot;{brandName}&quot; ve ÜYE bundan böyle birlikte &quot;Taraflar&quot;, ayrı
        ayrı &quot;Taraf&quot; olarak anılacaktır.
      </p>

      {/* MADDE 1 */}
      <p><strong>MADDE 1 – TANIMLAR</strong></p>
      <ul style={{ listStyleType: 'none', paddingLeft: 0 }}>
        <li>
          <strong>ÜYE:</strong> &quot;TATALLİ&quot; tarafından Platform&apos;un kullanıma sunulacağı
          web sitesi veya uygulamaya işbu Üyelik Sözleşmesi&apos;nde belirtilen koşullar dahilinde
          üye olan herhangi bir gerçek kişidir.
        </li>
        <li>
          <strong>{tenantBrandName} MOBİL UYGULAMASI:</strong> Platform&apos;u barındıran, Apple Store,
          Google Play Store, Huawei AppGallery gibi uygulama marketlerinden indirilebilen ve tüm
          hakları {companyName}&apos;ne ait mobil uygulamadır.
        </li>
        <li>
          <strong>MOBİL CİHAZ:</strong> Platform&apos;a ulaşmak için cep telefonu, tablet PC, akıllı
          saat gibi mobil ağın sunduğu hizmetlerden yararlanan cihazlardır.
        </li>
        <li>
          <strong>PLATFORM:</strong> Üyeler&apos;in Platform Hizmetleri&apos;nden faydalanabileceği{' '}
          {brandNameToOwn} ait aralarında {webSiteWithWww}&apos;un da bulunduğu internet siteleri ile
          mobil uygulamaları veya özel sanal sayfayı ifade eder.
        </li>
        <li>
          <strong>SATICI:</strong> TATALLİ ile yaptığı anlaşma kapsamında Platform&apos;da çeşitli
          ürün ve/veya hizmetleri satışa arz eden tüzel ya da gerçek kişilerdir.
        </li>
        <li>
          <strong>PLATFORM HİZMETLERİ:</strong> Satıcılar tarafından Platform üzerinde
          Üyeler&apos;in kullanımına sunulmuş veya ileride sunulacak olan çeşitli ürün ve/veya
          hizmetleri ifade eder.
        </li>
      </ul>

      {/* MADDE 2 */}
      <p><strong>MADDE 2 – SÖZLEŞMEYİN KONUSU, AMACI ve KAPSAMI</strong></p>
      <ul style={{ listStyleType: 'decimal' }}>
        <li>
          İşbu Sözleşme&apos;nin konusunu, Platform&apos;un ÜYE tarafından kullanılmasına ilişkin
          esas ve koşullar ile tarafların bu kapsamdaki hak ve yükümlülükleri oluşturmaktadır.
        </li>
        <li>
          Sözleşme&apos;nin ÜYE tarafından kabulü ile ÜYE Platform&apos;da yer alan ve yer alacak
          olan hizmetlere, kullanıma, içeriklere, uygulamalara ve üyelere yönelik her türlü beyanı
          ve bu beyanlara uygun davranacağını da kabul etmiş olmaktadır.
        </li>
        <li>
          TATALLİ, Platform&apos;u işletmekte olup, 6563 sayılı Elektronik Ticaretin Düzenlenmesi
          Hakkında Kanun uyarınca aracı hizmet sağlayıcıdır.
        </li>
        <li>
          Sözleşme uyarınca ÜYE, Platform&apos;a üye olarak Platform Hizmetleri&apos;nden
          faydalanmak istemektedir.
        </li>
        <li>
          Şüpheye mahal vermemek adına, işbu Sözleşme, yalnızca Taraflar arasında olup,
          Platform&apos;un kullanım koşullarını kapsamaktadır. TATALLİ, Platform&apos;da
          ÜYELER&apos;in kullanımına sunulmuş olan veya ileride sunulacak olan herhangi bir ürün
          veya hizmetin satıcısı/sağlayıcısı konumunda olmayıp ÜYE ile Satıcı arasındaki ilişki
          işbu Sözleşme&apos;nin kapsamına girmemektedir. TATALLİ, ÜYE ile Satıcı arasındaki
          ilişkiye hiçbir şekilde taraf değildir ve bu ilişkiden kaynaklanan hususlarda
          sorumluluğu bulunmamaktadır.
        </li>
        <li>
          ÜYE, Satıcı ile ÜYE arasında kurulacak sözleşmeye ilişkin TATALLİ&apos;nin herhangi bir
          garanti veya taahhüt vermediğini ve bu kapsamda oluşabilecek zararlardan
          TATALLİ&apos;nin sorumlu olmadığını kabul, beyan ve taahhüt eder.
        </li>
        <li>
          ÜYE Sözleşme&apos;yi okuduğunu, anladığını, haklarının ve yükümlülüklerinin bilincinde
          olduğunu kabul eder. ÜYE, işbu Sözleşme kapsamında yer alan işlemlerin kendi menfaatine
          uygun olduğu konusunda tam bir kanaate vardığını ve tüm şartlara kendi özgür iradesi ile
          uyacağını kabul eder.
        </li>
        <li>
          Taraflar, Sözleşme hükümlerinin tüketici sözleşmelerindeki haksız şartlar hakkında
          düzenleme hükümleri uyarınca haksız şart sayılabilecek bir özellik taşımadığını, menfaatler
          dengesi bakımından bir haksızlık olmadığını, dürüstlük ve iyi niyet kuralına aykırılık
          teşkil etmediğini ve Sözleşme&apos;nin tüketicinin korunması mevzuatına uygun olarak
          hazırlanmış olduğunu kabul eder.
        </li>
        <li>
          Taraflar, Sözleşme ile kararlaştırılan edimler arasında hiçbir oransızlık bulunmadığını ve
          karşılıklı edimlerin işin niteliğine uygun olduğunu, Sözleşme konusuna giren işlemler
          kapsamında herhangi bir tecrübesizliklerinin bulunmadığını kabul ederler.
        </li>
        <li>
          İşbu Sözleşme hükümleri Türk Borçlar Kanunu hükümleri de dikkate alınarak hazırlanmıştır.
          Türk Borçlar Kanunu&apos;nun 21. maddesinde öngörülen bağlayıcılık ve içerik denetimi ÜYE
          tarafından yapılmıştır. İşbu Sözleşme hükümlerinden hiçbiri Sözleşme&apos;nin niteliğine
          ve işin özelliğine yabancı (şaşırtıcı şartlar) nitelik taşımaz.
        </li>
      </ul>

      {/* MADDE 3 */}
      <p><strong>MADDE 3 – TARAFLARIN HAK VE YÜKÜMLÜLÜKLERİ</strong></p>
      <ul style={{ listStyleType: 'decimal' }}>
        <li>
          Platform&apos;a üye olabilmek için, 18 (onsekiz) yaşını doldurmak ve işbu
          Sözleşme&apos;nin ilgili hükümleri doğrultusunda TATALLİ tarafından geçici olarak
          üyelikten uzaklaştırılmamış veya üye olmaktan süresiz olarak yasaklanmamış olunması
          gerekmektedir. Üye olabilmek için gerekli bilgilerin kapsamı TATALLİ tarafından belirlenir
          ve gerekmesi halinde TATALLİ&apos;nin takdirinde olmak üzere değiştirilebilir veya kapsamı
          genişletilebilir. Bu bilgileri vermek istemeyen kişi Platform&apos;a ÜYE olamaz.
        </li>
        <li>
          ÜYE, Platform&apos;a üye olma veya sonradan yaptığı üyelik bilgisi güncelleme işlemleri
          esnasında verdiği tüm bilgilerin (kredi kartı/banka kartı bilgileri dahil) eksiksiz,
          gerçeğe uygun ve güncel olduğunu kabul, beyan ve taahhüt eder.
        </li>
        <li>
          Üye, Platform&apos;u indirip, kayıt için gerekli olan bölümleri doldurup bu
          Sözleşme&apos;ye taraf olduktan sonra işbu Sözleşme koşulları dâhilinde Platform&apos;da
          yer alan Platform Hizmetleri&apos;nden yararlanmaya başlayabilir. Şüpheye mahal vermemek
          adına, Platform Hizmetleri TATALLİ tarafından verilmemektedir.
        </li>
        <li>
          Üyelik kapsamında ÜYE&apos;nin aldığı şifreyi kullanma hakkı münhasıran ÜYE&apos;ye
          aittir. ÜYE bu şifreyi herhangi bir üçüncü şahsa veremez. Şifrenin ve Platform&apos;dan
          yararlanabilmek amacıyla kullanılan sisteme erişim araçlarının kullanımına ilişkin tüm
          hukuki ve cezai sorumluluk ÜYE&apos;ye aittir.
        </li>
        <li>
          ÜYE, Platform&apos;a erişim araçlarının kaybolması, çalınması veya üçüncü kişilerin eline
          geçmesi ya da bundan şüphe duyması halinde, TATALLİ&apos;nin çağrı merkezine ulaşarak
          derhal durumu bildirmekle yükümlüdür.
        </li>
        <li>
          ÜYE, Platform&apos;u kullanmasına imkan veren Mobil Cihaz&apos;ın tüm yazılımlarını
          (işletim sistemi dahil) güncel tutmayı ve Mobil Cihazı&apos;nda yetkisiz erişimlere karşın
          en güncel ve uygun antivirüs programları dahil her türlü internet korunma tedbirlerini
          alacağını kabul, beyan ve taahhüt eder.
        </li>
        <li>
          Üyelik hak ve yükümlülüklerinin hangi kişiye ait olduğu konusunda ihtilaf olması ve bu
          hususta söz konusu kişilerin TATALLİ&apos;den talepte bulunması halinde; TATALLİ ilgili
          üyelik hesabı kullanılarak herhangi bir hizmet için ödeme yapan en son kişinin üyelik
          hesabının sahibi olduğunu kabul ederek bu doğrultuda işlem yapma hakkına sahiptir.
        </li>
        <li>
          TATALLİ, gerekli gördüğü takdirde ÜYE&apos;ye haber vermeden Platform&apos;u
          güncelleyebilir ve daha önceki versiyonları üzerinden hizmet vermeyi durdurabilir.
          TATALLİ, kendi takdirinde olmak üzere, Platform&apos;u dilediği zaman kapatma hakkını
          haiz olduğu gibi Platform&apos;da sunulan Platform Hizmetleri&apos;ni süreli veya süresiz
          olarak kapatabilir.
        </li>
        <li>
          ÜYE, Platform&apos;u kullanarak yaptığı işlemlerde virüs, casus yazılım, kötü niyetli
          yazılım, truva atı ve benzeri şekilde Platform&apos;a zarar verecek nitelikteki
          materyallerin gönderilmemesi için gerekli her türlü tedbiri alacağını kabul, beyan ve
          taahhüt eder.
        </li>
        <li>
          ÜYE, Platform üzerinden yapacağı satın alma ve bunlarla ilgili ödeme işlemleri sırasında
          yaptığı tercihlerin doğuracağı sonuçlara ilişkin tüm sorumluluğun kendisine ait olduğunu
          kabul, beyan ve taahhüt eder.
        </li>
        <li>
          TATALLİ, Platform&apos;da yer alan herhangi bir ürün veya hizmetin satıcısı konumunda
          olmaması ve 6563 sayılı Elektronik Ticaretin Düzenlenmesi Hakkında Kanun uyarınca yalnızca
          &quot;aracı hizmet sağlayıcı&quot; olması sebebiyle, Platform&apos;da yayımlanan
          görsel, yazılı veya sair içeriğin gerçekliğinden, güvenilirliğinden, doğruluğundan ya da
          hukuka uygunluğundan sorumlu değildir.
        </li>
        <li>
          TATALLİ, ÜYE&apos;nin Platform&apos;a üyelik kapsamında verdiği bilgilerini ve Platform
          Hizmetleri&apos;nden yararlanırken yaptığı tercihlerini, herhangi bir ihtilaf, kötü niyetli
          davranış veya güvenlik endişesi durumunda, sistemin işlerliğinin sağlanması amacıyla
          denetleyebilir.
        </li>
        <li>
          ÜYE, Satıcı ile akdedeceği ön bilgilendirme formu, mesafeli satış sözleşmesi ve diğer
          akdedilebilecek bir belgeye ilişkin her türlü talebin tek muhatabının Satıcı olacağını;
          bu taleplerle ilgili TATALLİ&apos;nin herhangi bir yasal veya sözleşmesel sorumluluğu
          bulunmadığını kabul, beyan ve taahhüt eder.
        </li>
        <li>
          Platform&apos;da sergilenen ve satılan tüm ürün ve hizmetlerin kalitesinden, mevzuata
          uygunluğundan, garanti belgesi verilmesinden, faturalandırılmasından ve sair diğer gerekli
          belgenin tesliminden ve satış sonrası gereksinim duyulan servis vb. hizmetlerinden ve ürün
          ve hizmetlerin süresinde teslim edilmesinden Satıcılar bizzat ve münhasıran sorumlu olup
          TATALLİ&apos;nin bu anlamda bir sorumluluğu bulunmamaktadır.
        </li>
        <li>
          ÜYE tarafından varsa beyan edilen, yazılan fikir ve düşünceler, paylaşılan görüntüler,
          mesajlar, yorumlar ve kullanılan ifadeler münhasıran ÜYE&apos;nin kendi kişisel fikir ve
          yorumlarıdır ve bu fikir ve yorumların sonuçlarından yalnızca ÜYE sorumludur.
        </li>
        <li>
          TATALLİ, Platform&apos;dan gerçekleştirilen ve herhangi bir suç şüphesi oluşturan bir
          işlemi ÜYE&apos;ye önceden haber vermeksizin iptal etme veya üyeliği askıya alma hakkına
          sahip olup ÜYE&apos;nin bu durumu gerekçe göstererek TATALLİ&apos;den herhangi bir
          talepte bulunma hakkı bulunmamaktadır.
        </li>
        <li>
          Platform, TATALLİ&apos;nin kontrolünde bulunmayan diğer web sitelerine veya uygulamalara
          link, reklam veya ilan verebilir; bu sitelerin içeriğinden veya sunduğu ürün/hizmetlerden
          TATALLİ sorumlu değildir.
        </li>
        <li>
          ÜYE, Platform hizmetlerinden yararlanabilmek için üyelik bilgilerini doldururken aşağıdaki
          üye olma seçeneklerinden biri ile Platform&apos;a üye olabilir; şu kadar ki b) numaralı
          seçenekler yalnızca {companyName} müşterileri için geçerlidir:
          <ul style={{ listStyleType: 'lower-latin' }}>
            <li>
              Üyelik kaydı esnasında ÜYE&apos;nin cep telefonu ya da e-posta adresi kullanarak
              kendisinin oluşturduğu {brandName} kullanıcı adı ve uygulama şifresi,
            </li>
            <li>
              {companyName}&apos;nin müşteri numarası/T.C. kimlik numarası/yabancı kimlik numarası
              ve {companyName}&apos;nin müşteri şifresi/uygulama şifresi veya
            </li>
            <li>Facebook, Google gibi platformlar aracılığıyla otantikasyon</li>
          </ul>
        </li>
        <li>
          ÜYE, Platform&apos;a kaydettiği banka kartları, kredi kartları veya diğer ödeme
          vasıtaları dahil bunlarla ilgili tüm bilgilerin sisteme doğru kaydedilmesinden kendisinin
          sorumlu olduğunu kabul, beyan ve taahhüt eder.
        </li>
        <li>
          ÜYE, Platform&apos;u aşağıda sayılan haller dahil yürürlükteki mevzuat hükümlerine,
          ahlaka veya etik değerlere aykırı bir şekilde kullanmayacaktır:
          <ul style={{ listStyleType: 'lower-latin' }}>
            <li>Platform&apos;un herhangi bir kişi adına veri tabanı, kayıt veya rehber yaratmak amacıyla kullanılması;</li>
            <li>Platform&apos;un kamu düzenini bozucu, genel ahlaka aykırı, başkalarını rahatsız ve taciz edici şekilde kullanılması;</li>
            <li>Platform&apos;un bütününün veya bir bölümünün bozma, değiştirme veya ters mühendislik yapma amacıyla kullanılması;</li>
            <li>Yanlış bilgiler veya başka bir kişinin bilgileri kullanılarak gerçek olmayan üye hesapları oluşturulması ve bu hesapların mevzuata aykırı şekilde kullanılması;</li>
            <li>Yorum ve puanlama sistemlerinin manipüle edilecek şekilde kullanılması;</li>
            <li>Kullanıcılar veya satıcılar hakkında herhangi bir bilginin ilgili kişilerin izni olmaksızın toplanması;</li>
            <li>Platform üzerindeki kişisel verilerin elde edilmesine yönelik eylemde bulunulması;</li>
            <li>TATALLİ&apos;nin önceden yazılı iznini almaksızın Platform üzerinde otomatik program, robot, web crawler, veri madenciliği gibi &quot;screen scraping&quot; yazılımları kullanılması.</li>
          </ul>
        </li>
        <li>
          ÜYE, işbu Sözleşme&apos;nin; TATALLİ ile arasındaki Sözleşme konusuna ilişkin tek
          sözleşme olduğunu ve daha önce varsa akdedilmiş olan tüm sözleşmelerin yerine geçeceğini
          kabul, beyan ve taahhüt eder.
        </li>
        <li>
          ÜYE, üyelikten dilediği zaman çıkabilir. Bunun için Platform&apos;da bulunan üyelik iptal
          fonksiyonunu kullanacaktır.
        </li>
        <li>
          TATALLİ tarafından işletilen Platform&apos;u barındıran ve mülkiyeti {companyName}&apos;ye
          ait olan {companyMobileApp}&apos;nda Platform Hizmetleri&apos;ne elektronik ticaret ortamı
          sağlayan Platform&apos;un yanı sıra {companyName}&apos;ne ait bazı ürün ve hizmetler de
          ÜYELER&apos;in kullanımına sunulmaktadır. ÜYE, bu Sözleşme&apos;nin yalnızca
          Platform&apos;un kullanım koşulları ile ilgili olduğunu kabul ve beyan eder.
        </li>
        <li>
          ÜYE, Satıcılar ile arasında oluşan uyuşmazlıklarda TATALLİ&apos;nin herhangi bir görevi
          veya sorumluluğu olmadığını, bu konulara istinaden tek muhatabının Satıcı olduğunu kabul
          ve beyan eder.
        </li>
        <li>
          Platform üzerinden gerçekleştirilebilen işlemler, Platform ve diğer her türlü içerik,
          veri, belge ve malzeme, &quot;olduğu gibi&quot; ve &quot;olduğu şekilde&quot; ÜYE&apos;ye
          sunulmuş olup herhangi bir taahhüt ve garanti içermemektedir. TATALLİ,
          Platform&apos;un kusursuz, kesintisiz ve tam olarak virüsten ari güvenli bir şekilde
          temin edileceğini garanti etmemektedir.
        </li>
      </ul>

      {/* MADDE 4 */}
      <p><strong>MADDE 4 – SONA ERME ve TAZMİNAT</strong></p>
      <ul style={{ listStyleType: 'decimal' }}>
        <li>
          Taraflardan herhangi biri, işbu Üyelik Sözleşmesi&apos;ni tek taraflı olarak ve tazminat
          ödemeksizin her zaman feshedebilir. Böyle bir fesih halinde Taraflar fesih tarihine kadar
          doğmuş olan hak ve borçları karşılıklı olarak tamamen ifa edeceklerdir. ÜYE&apos;nin
          fesih işlemi Platform üzerinden üyelik iptal fonksiyonu kullanılmak suretiyle yapılır.
        </li>
        <li>
          TATALLİ&apos;nin, ÜYE&apos;nin işbu Sözleşme&apos;de belirtilen yükümlülüklerine aykırı
          davrandığını veya Platform&apos;da sunulan hizmetleri kötüye kullandığını tespit etmesi
          veya buna ilişkin makul bir şüphe duyması halinde, ÜYE&apos;nin üyeliğini askıya alma
          veya sonlandırma hakkı bulunmaktadır. ÜYE bu hallerde TATALLİ&apos;nin ve/veya üçüncü
          kişilerin doğmuş veya doğacak her türlü zararını tazmin edeceğini kabul, beyan ve
          taahhüt eder.
        </li>
        <li>
          TATALLİ, herhangi bir şekilde Platform&apos;un ÜYE tarafından kullanılmasından, Platform
          Hizmetleri&apos;nden faydalanılmasından, işbu Sözleşme&apos;den, Platform&apos;dan veya
          burada bulunan işlemlerden kaynaklı veya bunlarla ilgili olarak doğan herhangi bir zarardan
          (veri kaybı, kazanç kaybı ve bunlarla sınırlı olmaksızın) sorumlu olmayacaktır.
        </li>
        <li>
          TATALLİ&apos;nin işbu Sözleşme&apos;ye ÜYE&apos;nin aykırı davranması sonucu ilgili hak
          sahipleri tarafından gelecek olan tüm davalardan ve/veya taleplerden doğacak olan avukatlık
          ücreti, tazminat ve diğer sair taleplerini ÜYE&apos;ye rücü etme hakkı her zaman
          mevcuttur.
        </li>
      </ul>

      {/* MADDE 5 */}
      <p><strong>MADDE 5 – DEĞİŞİKLİKLER</strong></p>
      <ul style={{ listStyleType: 'decimal' }}>
        <li>
          TATALLİ, tamamen kendi takdirine bağlı olmak üzere, işbu Sözleşme&apos;yi ve
          Platform&apos;da yer alan Gizlilik Politikası ve Çerez (Cookie) Politikası da dahil her
          türlü politikayı, hüküm ve şartı uygun göreceği herhangi bir zamanda, yürürlükteki
          mevzuat hükümlerine aykırı olmamak kaydıyla Platform&apos;da ilan ederek tek taraflı
          olarak değiştirebilir.
        </li>
        <li>
          TATALLİ, Platform&apos;un içeriğini, tasarımını ve yazılımını dilediği zaman değiştirme,
          ÜYE&apos;ye sağlanan herhangi bir hizmeti değiştirme, durdurma ya da sonlandırma ve
          dilediği zaman internet sitesinde veya Platform&apos;da kayıtlı olan kullanıcı bilgi ve
          verilerini silme hakkını saklı tutar.
        </li>
      </ul>

      {/* MADDE 6 */}
      <p><strong>MADDE 6 – MÜCBİR SEBEPLER</strong></p>
      <p>
        Hukuken mücbir sebep sayılan bütün durumlar dahil olmak üzere siber saldırı, iletişim
        sorunları, altyapı ve internet arızaları, sisteme ilişkin iyileştirme veya yenileştirme
        çalışmaları ve bu sebeple meydana gelebilecek arıza hallerinde, tabii afetler, yangın,
        patlamalar, iç savaşlar, savaşlar, ayaklanmalar, halk hareketleri, seferberlik ilanı,
        grev, lokavt ve salgın hastalıklar ve herhangi bir resmi makamın yasama ve idari işlemleri
        gibi sebeplerden kaynaklanan ve doğrudan TATALLİ&apos;nin akdi yükümlülüklerini yerine
        getirmesini imkansız hale getiren söz konusu sebepler, TATALLİ&apos;nin işbu sözleşmeden
        doğan yükümlülüklerini ifa etmesini engeller veya geciktirirse, TATALLİ ifası Mücbir Sebep
        sonucunda engellenen veya geciken yükümlülüklerinden dolayı sorumlu tutulamaz ve bu durum
        işbu Sözleşme&apos;nin bir ihlali olarak kabul edilemez.
      </p>

      {/* MADDE 7 */}
      <p><strong>MADDE 7 – FİKRİ MÜLKİYET HAKLARI</strong></p>
      <ul style={{ listStyleType: 'decimal' }}>
        <li>
          İşbu madde kapsamında &quot;Fikri Mülkiyet Hakları&quot;; TATALLİ&apos;nin sahip olduğu
          veya kullandığı tescil edilmemiş, tescili beklenen veya tescil edilmiş her türlü dünya
          çapında mevcut veya gelecekte mevcut olacak ticaret markasını, tüm ticari unvanları,
          hizmet markalarını, patentleri, ticaret, faaliyet ve alan adlarını, URL&apos;leri,
          tasarımları, telif haklarını, spesifikasyonları ve yazılımları ifade eder.
        </li>
        <li>
          ÜYE, Platform üzerindeki ve Platform ile bağlantılı materyal üzerindeki her türlü Fikri
          Mülkiyet Hakları&apos;nın TATALLİ&apos;ye ait olduğunu kabul etmektedir.
        </li>
        <li>
          İşbu Sözleşme&apos;de geçen hiçbir hüküm TATALLİ&apos;ye ait Fikri Mülkiyet
          Hakları&apos;nın ÜYE&apos;ye veya Satıcı&apos;ya devredildiği anlamına gelmez.
        </li>
        <li>
          Platform ve Platform&apos;da sunulan hizmetler, ÜYE tarafından hiçbir surette iktisap
          edilemez, değiştirilemez, kopyalanamaz, paylaşılamaz, çoğaltılamaz, 3. kişilerin
          erişimine sunulamaz, ters mühendislik yapılamaz, yayınlanamaz veya 3. kişilere satılamaz.
        </li>
        <li>
          İşbu Sözleşme&apos;nin imzalanmasıyla ÜYE, doğrudan veya dolaylı surette, Platform
          ve/veya benzeri formatta yazılım, tasarım, donanım oluşturmayacağını, ters mühendislik,
          işleme, değiştirme, geliştirme veya sair bir yol ile kendi adına ve/veya hiçbir surette
          3. kişiler menfaatine bu işlemlere girişmeyeceğini kabul, beyan ve taahhüt eder.
        </li>
        <li>
          ÜYE, Platform ve/veya Platform&apos;da sunulan hizmetlerin herhangi bir yolla taklit
          edilmeyeceğini, bunlara ait olan her türlü bilgiyi gizli bilgi olarak saklayacağını ve
          ifşa etmeyeceğini kabul, beyan ve taahhüt eder.
        </li>
        <li>
          ÜYE&apos;nin üçüncü kişilerin veya TATALLİ&apos;nin Fikri Mülkiyet Hakları&apos;nı
          ihlal edecek şekilde davranması halinde, ÜYE, TATALLİ&apos;nin ve/veya söz konusu üçüncü
          kişinin tüm doğrudan ve dolaylı zararları ile masraflarını tazmin etmekle yükümlüdür.
        </li>
      </ul>

      {/* MADDE 8 */}
      <p><strong>MADDE 8 – GİZLİLİK ve KİŞİSEL VERİLERİN KORUNMASI</strong></p>
      <ul style={{ listStyleType: 'decimal' }}>
        <li>
          TATALLİ, ÜYE&apos;nin Platform&apos;da sunulan hizmetlerden yararlanabilmek için verdiği
          kişisel verilerin 6698 sayılı Kişisel Verilerin Korunması Kanunu ve ikincil düzenlemelere
          uygun bir şekilde işlenmesine, güvenliğinin sağlanmasına ve korunmasına önem vermektedir.
          Gizlilik Politikası ve Çerez Politikası dahil TATALLİ tarafından Platform ile ilgili
          oluşturulan tüm politikalar, işbu Sözleşme&apos;nin ayrılmaz bir parçasıdır.
        </li>
        <li>
          ÜYE tarafından Platform&apos;da üyelik oluşturmak veya Platform&apos;dan faydalanmak
          amacıyla paylaşılan kişisel veriler; Üyelik Sözleşmesi ile belirlenen yükümlülüklerin
          ifası, Platform&apos;un işletilmesi için gereken uygulamaların yürütülmesi, ÜYE&apos;ye
          çeşitli avantajların sağlanıp sunulabilmesi, ödeme işlemlerinin gerçekleştirilmesi,
          sipariş teslimatlarının yapılması, müşteri hizmetleri ve şikayet takibi işlemlerinin
          gerçekleştirilmesi ve ÜYE&apos;ye özel reklam, satış, pazarlama, anket, benzer amaçlı
          her türlü elektronik iletişim, profilleme, istatistiksel çalışmalar yapılması amacıyla
          TATALLİ ya da iş ortakları tarafından toplanmakta, saklanmakta ve işlenmektedir.
        </li>
        <li>
          <span>
            ÜYE, TATALLİ&apos;ye aktardığı, kaydettiği, depoladığı ve muhafaza ettiği kişisel
            veriler de dahil olmak üzere herhangi bir şekilde işleyebileceği her türlü kişisel veri
            ile ilgili olarak;
          </span>
          <ul style={{ listStyleType: 'lower-latin' }}>
            <li>Yürürlükteki mevzuat hükümlerine uygun şekilde, 3. kişiye ilişkin işleyeceği veriler için ilgili veri sahibine karşı aydınlatma yükümlülüğünü, gerekliyse açık rıza alma yükümlülüğünü yerine getireceğini ve mevzuata uygun davranacağını,</li>
            <li>Kişisel verilerin işlenmesine ve/veya aktarımına ilişkin olarak TATALLİ&apos;nin menfaatlerini ilgilendirebilecek herhangi bir ihtilaf yahut konu nedeniyle kendisine yöneltilmiş olan tüm talepler konusunda TATALLİ&apos;yi derhal bilgilendireceğini ve TATALLİ ile tam bir iş birliği yapacağını,</li>
            <li>Kişisel verilerin hukuka aykırı olarak işlenmesini önlemek, kişisel verilere hukuka aykırı olarak erişilmesini önlemek, kişisel verilerin muhafazasını sağlamak amacıyla uygun güvenlik düzeyini temin etmeye yönelik gerekli her türlü teknik ve idari tedbiri alacağını,</li>
          </ul>
          <span>kabul, beyan ve taahhüt eder.</span>
        </li>
      </ul>

      {/* MADDE 9 */}
      <p><strong>MADDE 9 – MUHTELİF HÜKÜMLER</strong></p>
      <ul style={{ listStyleType: 'decimal' }}>
        <li>
          <strong>Üyelik Sözleşmesi&apos;nin Bütünlüğü ve Bölünebilirliği:</strong> İşbu Sözleşme,
          konuya ilişkin olarak Taraflar arasındaki anlaşmanın tamamını oluşturmaktadır. İşbu
          Sözleşme&apos;nin herhangi bir hükmünün yetkili herhangi bir mahkeme, tahkim heyeti veya
          idari makam tarafından tümüyle veya kısmen geçersiz veya uygulanamaz olduğu veya makul
          olmadığına karar verilmesi halinde, söz konusu geçersizlik ölçüsünde işbu Sözleşme
          bölünebilir olarak kabul edilecek ve diğer hükümler tümüyle yürürlükte kalmaya devam
          edecektir.
        </li>
        <li>
          <strong>Hukuki Giderler:</strong> ÜYE, hakkında yasal yollara başvurulması, kanuni takip
          yapılması vb. hukuki uyuşmazlıklar yaşanması halinde Hukuk Muhakemeleri Kanunu, İcra ve
          İflas Kanunu, Harçlar Kanunu, Avukatlık Asgari Ücret Tarifesi ve ilgili mevzuat uyarınca
          ortaya çıkabilecek her türlü vergi, resim, harç dahil tüm masraf ve giderleri
          TATALLİ&apos;ye ödeyeceğini kabul, beyan ve taahhüt eder.
        </li>
        <li>
          <strong>Uygulanacak Hukuk ve Yetkili Mahkeme:</strong> İşbu Sözleşme münhasıran Türkiye
          Cumhuriyeti kanunlarına tabi olacak ve Sözleşme&apos;den kaynaklanan veya Sözleşme ile
          bağlantılı olan her türlü ihtilaf, İstanbul Merkez (Çağlayan) Mahkemeleri ve İcra
          Müdürlükleri&apos;nin münhasır yargı yetkisinde olacaktır.
        </li>
        <li>
          <strong>Delil Sözleşmesi:</strong> ÜYE, işbu Sözleşme nedeniyle çıkabilecek her türlü
          uyuşmazlığın çözümünde, TATALLİ&apos;nin defterleri ile TATALLİ&apos;deki her türlü
          kaydın, bilgisayar kayıtlarının, mikrofilm, mikrofiş vb.nin geçerli ve kesin delil teşkil
          edeceğini ve bu hükmün Hukuk Muhakemeleri Kanunu&apos;nun 193. maddesi uyarınca bir delil
          sözleşmesi niteliğinde olacağını kabul, beyan ve taahhüt eder.
        </li>
        <li>
          <strong>Bildirim:</strong> TATALLİ, ÜYE&apos;nin kayıt olurken bildirmiş olduğu
          elektronik posta adresi vasıtasıyla veya telefon numarasına arama yapmak ve SMS
          göndermek suretiyle iletişim kuracaktır. Üye, elektronik posta adresini ve telefon
          numarasını güncel tutmakla yükümlüdür.
        </li>
        <li>
          <strong>Sözleşme&apos;nin Devri:</strong> ÜYE, TATALLİ&apos;nin önceden yazılı onayını
          almaksızın işbu Sözleşme&apos;deki haklarını veya yükümlülüklerini tümüyle veya kısmen
          temlik edemeyecektir.
        </li>
        <li>
          <strong>Feragat:</strong> Taraflardan birinin Sözleşme&apos;de kendisine verilen herhangi
          bir hakkı kullanmaması ya da icra etmemesi, söz konusu haktan feragat ettiği anlamına
          gelmeyecek veya söz konusu hakkın daha sonra kullanılmasını ya da icra edilmesini
          engellemeyecektir.
        </li>
        <li>
          <strong>Sözleşme ve Eklerinin Bütünlüğü:</strong> Sözleşmenin ekleri bu
          Sözleşme&apos;nin ayrılmaz bir parçasını teşkil edecek olup birbirlerinden ayrı olarak
          yorumlanamaz.
        </li>
        <li>
          <strong>İletişim Bilgileri:</strong> TATALLİ&apos;nin iletişim bilgileri aşağıdaki
          gibidir:<br />
          Adres: {companyAddress}<br />
          E-posta: {companyEmail}<br />
          Telefon: {companyPhone}<br />
          Kep: {companyKep}
        </li>
        <li>
          <strong>Taraflar Arasındaki İlişki:</strong> Taraflar arasındaki ilişki bağımsızdır. İşbu
          Sözleşme&apos;deki hiçbir durum, Taraflar arasında işveren/çalışan, imtiyaz
          veren/imtiyaz alan, amir/vekil, ortaklık veya müşterek girişim oluşturacak veya teşkil
          edecek şekilde yorumlanmayacaktır.
        </li>
        <li>
          <strong>Platform Üzerinden Yapılan İşlemler:</strong> Platform üzerinden yapılan ve irade
          açıklaması niteliğindeki işlemler Türk Borçlar Kanunu, tüketici mevzuatı ve yürürlükte
          bulunan sair mevzuat uyarınca Taraflar&apos;ı bağlayıcı irade açıklamaları olarak telakki
          edilirler. 9 (dokuz) maddeden ibaret bu Sözleşme, ÜYE tarafından her bir hükmü okunarak
          ve bütünüyle anlaşılarak elektronik ortamda onaylanmak suretiyle yürürlüğe girmiştir.
        </li>
      </ul>
    </>
  );
}